import { useEffect } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';

// Back from the first page after a Microsoft sign-in stays on that page
// (docs/Auth-Flows.md, "Back after a sign-in"). The history behind it holds
// Microsoft's own pages (email code, password), and going back into them fails.
//
// EntraCallback lands with ENTRA_LANDING_STATE. On that first arrival this hook
// pushes the same address again, unmarked. Back then pops to the marked copy,
// which only steps forward again: the page does not change. The app uses
// <BrowserRouter>, so react-router's useBlocker is not available.

export const ENTRA_LANDING_STATE = { entraLanding: true };

type LandingState = { entraLanding?: boolean } | null;

// React Router keeps the state it was given under `usr`.
const currentEntryIsLanding = () => Boolean(
  (window.history.state as { usr?: LandingState } | null)?.usr?.entraLanding
);

export const useEntraBackGuard = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const navigate = useNavigate();

  useEffect(() => {
    if (!(location.state as LandingState)?.entraLanding) return;
    // The landing page may have redirected on (e.g. "/" to /homepage) in the
    // same render; act only while the marked entry is still the current one.
    if (!currentEntryIsLanding()) return;
    if (navigationType === 'POP') {
      window.history.forward();
      return;
    }
    navigate(`${location.pathname}${location.search}${location.hash}`);
  }, [location, navigationType, navigate]);
};
